import { Edit, Save, Trash2, X } from 'lucide-react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { formatManagementServicePlan } from '../../components/BillingManagement/ManagementServicePlanPolicy';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const UnitManagementModal = ({ unit, buildingId, isOpen, onClose, onUnitUpdated }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  
  // 세대 상세 정보 다시 가져오기 (최신 데이터 보장)
  // 주석: API 호출이 실패해도 prop으로 받은 unit 데이터를 사용하므로 에러는 무시
  const { data: unitDetail, isLoading: unitDetailLoading } = useQuery(
    ['unit-detail', unit?.id],
    () => api.get(`/buildings/units/${unit.id}`),
    {
      enabled: isOpen && !!unit?.id,
      retry: false // API 실패 시 재시도하지 않음 (prop 데이터 사용)
    }
  );
  
  // unitDetail이 있으면 그것을 사용, 없으면 prop으로 받은 unit 사용
  const displayUnit = unitDetail?.unit || unit;
  

  // 요금플랜 목록 조회 (RMC 사용자만)
  const { data: servicePlansData, isLoading: plansLoading } = useQuery(
    'management-service-plans',
    async () => {
      const res = await api.get('/management-service-billing/plans');
      return res.plans || res || [];
    },
    {
      enabled: (currentUser?.role === 'rental_management_company' || currentUser?.role === 'rmc_admin' || currentUser?.role === 'rmc_staff') && isEditing,
      onError: (error) => {
        console.error('요금플랜 조회 실패:', error);
      }
    }
  );

  const servicePlans = servicePlansData || [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm({
    defaultValues: {
      unit_number: unit?.unit_number || '',
      floor: unit?.floor || '',
      area: unit?.area || '',
      unit_type: unit?.unit_type || '',
      monthly_rent: unit?.monthly_rent || '',
      deposit: unit?.deposit || '',
      maintenance_fee: unit?.maintenance_fee || '',
      status: unit?.status || 'available',
      vacancy_electric_fee: unit?.vacancy_electric_fee || '',
      vacancy_water_fee: unit?.vacancy_water_fee || '',
      vacancy_gas_fee: unit?.vacancy_gas_fee || '',
      vacancy_common_fee: unit?.vacancy_common_fee || '',
      vacancy_other_fee: unit?.vacancy_other_fee || '',
      vacancy_cost_payer: unit?.vacancy_cost_payer || 'property_owner',
      service_plan_id: unit?.service_plan_id || ''
    }
  });

  // 세대 수정 뮤테이션
  const updateUnitMutation = useMutation(
    (data) => api.put(`/buildings/${buildingId}/units/${unit.id}`, data),
    {
      onSuccess: () => {
        toast.success('세대 정보가 성공적으로 수정되었습니다.');
        queryClient.invalidateQueries(['units', buildingId]);
        setIsEditing(false);
        if (onUnitUpdated) onUnitUpdated();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '세대 수정에 실패했습니다.');
      }
    }
  );

  // 세대 삭제 뮤테이션
  const deleteUnitMutation = useMutation(
    () => api.delete(`/buildings/${buildingId}/units/${unit.id}`),
    {
      onSuccess: () => {
        toast.success('세대가 성공적으로 삭제되었습니다.');
        queryClient.invalidateQueries(['units', buildingId]);
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '세대 삭제에 실패했습니다.');
        setIsDeleting(false);
      }
    }
  );

  const onSubmit = (data) => {
    updateUnitMutation.mutate(data);
  };

  const handleEdit = () => {
    setIsEditing(true);
    const currentUnit = displayUnit || unit;
    reset({
      unit_number: currentUnit?.unit_number || '',
      floor: currentUnit?.floor || '',
      area: currentUnit?.area || '',
      unit_type: currentUnit?.unit_type || '',
      monthly_rent: currentUnit?.monthly_rent || '',
      deposit: currentUnit?.deposit || '',
      maintenance_fee: currentUnit?.maintenance_fee || '',
      status: currentUnit?.status || 'available',
      vacancy_electric_fee: currentUnit?.vacancy_electric_fee || '',
      vacancy_water_fee: currentUnit?.vacancy_water_fee || '',
      vacancy_gas_fee: currentUnit?.vacancy_gas_fee || '',
      vacancy_common_fee: currentUnit?.vacancy_common_fee || '',
      vacancy_other_fee: currentUnit?.vacancy_other_fee || '',
      vacancy_cost_payer: currentUnit?.vacancy_cost_payer || 'property_owner',
      service_plan_id: currentUnit?.service_plan_id || ''
    });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    reset();
  };

  const handleDelete = () => {
    const unitNumber = displayUnit?.unit_number || unit?.unit_number || '';
    if (window.confirm(`"${unitNumber}호" 세대를 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`)) {
      setIsDeleting(true);
      deleteUnitMutation.mutate();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 text-green-800';
      case 'occupied':
        return 'bg-blue-100 text-blue-800';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800';
      case 'reserved':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'available':
        return '임대 가능';
      case 'occupied':
        return '임대중';
      case 'maintenance':
        return '정비중';
      case 'reserved':
        return '예약중';
      default:
        return '알 수 없음';
    }
  };

  if (!isOpen || !unit) return null;

  if (unitDetailLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 p-6">
          <div className="text-center">세대 정보를 불러오는 중...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditing ? '세대 정보 수정' : '세대 상세 정보'}
            </h2>
          </div>
          <div className="flex items-center space-x-2">
            {!isEditing && (
              <>
                <button
                  onClick={handleEdit}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <Edit className="mr-1 h-4 w-4" />
                  수정
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  <Trash2 className="mr-1 h-4 w-4" />
                  {isDeleting ? '삭제 중...' : '삭제'}
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* 내용 */}
        <div className="p-6">
          {isEditing ? (
            // 수정 폼
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* 기본 정보 */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">기본 정보</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      호수 *
                    </label>
                    <input
                      type="text"
                      {...register('unit_number', { required: '호수를 입력해주세요' })}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.unit_number ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.unit_number && (
                      <p className="mt-1 text-sm text-red-600">{errors.unit_number.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      층수
                    </label>
                    <input
                      type="number"
                      {...register('floor', {
                        min: { value: 1, message: '올바른 층수를 입력해주세요' }
                      })}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.floor ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.floor && (
                      <p className="mt-1 text-sm text-red-600">{errors.floor.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      면적 (㎡)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('area', {
                        min: { value: 0, message: '올바른 면적을 입력해주세요' }
                      })}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.area ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.area && (
                      <p className="mt-1 text-sm text-red-600">{errors.area.message}</p>
                    )}
                  </div>



                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      세대 유형
                    </label>
                    <select
                      {...register('unit_type')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">세대 유형 선택</option>
                      <option value="원룸">원룸</option>
                      <option value="1.5룸">1.5룸</option>
                      <option value="투룸">투룸</option>
                      <option value="쓰리룸 이상">쓰리룸 이상</option>
                    </select>
                  </div>
                </div>

                                 {/* 임대 정보 */}
                 <div className="space-y-4">
                   <h3 className="text-lg font-medium text-gray-900">기준임대료</h3>
                   
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">
                       기준월세 (원)
                     </label>
                     <input
                       type="number"
                       {...register('monthly_rent', {
                         min: { value: 0, message: '올바른 금액을 입력해주세요' }
                       })}
                       className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                         errors.monthly_rent ? 'border-red-300' : 'border-gray-300'
                       }`}
                     />
                     {errors.monthly_rent && (
                       <p className="mt-1 text-sm text-red-600">{errors.monthly_rent.message}</p>
                     )}
                   </div>

                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">
                       기준보증금 (원)
                     </label>
                     <input
                       type="number"
                       {...register('deposit', {
                         min: { value: 0, message: '올바른 금액을 입력해주세요' }
                       })}
                       className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                         errors.deposit ? 'border-red-300' : 'border-gray-300'
                       }`}
                     />
                     {errors.deposit && (
                       <p className="mt-1 text-sm text-red-600">{errors.deposit.message}</p>
                     )}
                   </div>

                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">
                       기준관리비 (원)
                     </label>
                     <input
                       type="number"
                       {...register('maintenance_fee', {
                         min: { value: 0, message: '올바른 금액을 입력해주세요' }
                       })}
                       className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                         errors.maintenance_fee ? 'border-red-300' : 'border-gray-300'
                       }`}
                     />
                     {errors.maintenance_fee && (
                       <p className="mt-1 text-sm text-red-600">{errors.maintenance_fee.message}</p>
                     )}
                   </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      상태
                    </label>
                    <select
                      {...register('status')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="available">임대 가능</option>
                      <option value="occupied">임대중</option>
                      <option value="maintenance">정비중</option>
                      <option value="reserved">예약중</option>
                    </select>
                  </div>

                  {/* 임대관리수수료 요금플랜 (RMC 사용자만) */}
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <h4 className="mb-3 text-sm font-semibold text-gray-900">공실 기본비용</h4>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {[
                        ['vacancy_electric_fee', '전기'],
                        ['vacancy_water_fee', '수도'],
                        ['vacancy_gas_fee', '가스'],
                        ['vacancy_common_fee', '공용'],
                        ['vacancy_other_fee', '기타']
                      ].map(([field, label]) => (
                        <div key={field}>
                          <label className="mb-1 block text-xs font-medium text-gray-600">{label} (원/월)</label>
                          <input
                            type="number"
                            min="0"
                            {...register(field, {
                              min: { value: 0, message: '0원 이상 입력해주세요' }
                            })}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                      ))}
                    </div>
                    <div className="mt-3">
                      <label className="mb-1 block text-xs font-medium text-gray-600">부담 주체</label>
                      <select
                        {...register('vacancy_cost_payer')}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="property_owner">임대사업자 부담</option>
                        <option value="rental_manager">임대관리자 부담</option>
                      </select>
                    </div>
                  </div>

                  {(currentUser?.role === 'rental_management_company' || currentUser?.role === 'rmc_admin' || currentUser?.role === 'rmc_staff') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        임대관리수수료 요금플랜 <span className="text-xs text-gray-500">(선택사항)</span>
                      </label>
                      <select
                        {...register('service_plan_id')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        disabled={plansLoading}
                      >
                        <option value="">요금플랜 선택 안함</option>
                        {servicePlans.length > 0 ? (
                          servicePlans
                            .filter(plan => plan.is_active)
                            .map(plan => (
                              <option key={plan.id} value={plan.id}>
                                [{plan.plan_category || 'regular'}] {plan.plan_name} ({formatManagementServicePlan(plan)})
                              </option>
                            ))
                        ) : (
                          <option value="" disabled>요금플랜 없음</option>
                        )}
                      </select>
                      {plansLoading && (
                        <p className="mt-1 text-xs text-blue-600">요금플랜 목록을 불러오는 중...</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* 버튼 */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={updateUnitMutation.isLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {updateUnitMutation.isLoading ? '저장 중...' : '저장'}
                </button>
              </div>
            </form>
          ) : (
            // 상세보기
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* 기본 정보 */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">기본 정보</h3>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">호수</span>
                      <span className="text-sm font-medium text-gray-900">{displayUnit?.unit_number || '-'}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">층수</span>
                      <span className="text-sm font-medium text-gray-900">{displayUnit?.floor ? `${displayUnit.floor}층` : '-'}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">면적</span>
                      <span className="text-sm font-medium text-gray-900">{displayUnit?.area ? `${displayUnit.area}㎡` : '-'}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">방 개수</span>
                      <span className="text-sm font-medium text-gray-900">{displayUnit?.room_count ? `${displayUnit.room_count}개` : '-'}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">세대 유형</span>
                      <span className="text-sm font-medium text-gray-900">{displayUnit?.unit_type || '-'}</span>
                    </div>
                  </div>
                </div>

                                 {/* 임대 정보 */}
                 <div className="space-y-4">
                   <h3 className="text-lg font-medium text-gray-900">기준임대료</h3>
                   
                   <div className="space-y-3">
                     <div className="flex justify-between">
                       <span className="text-sm text-gray-500">기준월세</span>
                       <span className="text-sm font-medium text-gray-900">
                         {displayUnit?.standard_monthly_rent ? `${Number(displayUnit.standard_monthly_rent).toLocaleString()}원` : (displayUnit?.standard_monthly_rent === 0 ? '0원' : '-')}
                       </span>
                     </div>
                     
                     <div className="flex justify-between">
                       <span className="text-sm text-gray-500">기준보증금</span>
                       <span className="text-sm font-medium text-gray-900">
                         {displayUnit?.standard_deposit ? `${Number(displayUnit.standard_deposit).toLocaleString()}원` : (displayUnit?.standard_deposit === 0 ? '0원' : '-')}
                       </span>
                     </div>
                     
                     <div className="flex justify-between">
                       <span className="text-sm text-gray-500">기준관리비</span>
                       <span className="text-sm font-medium text-gray-900">
                         {displayUnit?.standard_maintenance_fee ? `${Number(displayUnit.standard_maintenance_fee).toLocaleString()}원` : (displayUnit?.standard_maintenance_fee === 0 ? '0원' : '-')}
                       </span>
                     </div>
                    
                    <div className="rounded-md bg-gray-50 p-3">
                      <div className="mb-2 flex justify-between text-sm">
                        <span className="font-medium text-gray-700">공실 기본비용</span>
                        <span className="text-gray-600">
                          {(displayUnit?.vacancy_cost_payer || 'property_owner') === 'rental_manager'
                            ? '임대관리자 부담'
                            : '임대사업자 부담'}
                        </span>
                      </div>
                      {[
                        ['전기', displayUnit?.vacancy_electric_fee],
                        ['수도', displayUnit?.vacancy_water_fee],
                        ['가스', displayUnit?.vacancy_gas_fee],
                        ['공용', displayUnit?.vacancy_common_fee],
                        ['기타', displayUnit?.vacancy_other_fee]
                      ].map(([label, value]) => (
                        <div key={label} className="flex justify-between text-sm">
                          <span className="text-gray-500">{label}</span>
                          <span className="font-medium text-gray-900">{Number(value || 0).toLocaleString()}원</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">상태</span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(displayUnit?.status || 'available')}`}>
                        {getStatusText(displayUnit?.status || 'available')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 빌링 관련 정보 (향후 확장용) */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">빌링 기초 정보</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">
                    이 세대의 정보는 향후 빌링 시스템의 기초 데이터로 활용됩니다.
                  </p>
                  <div className="mt-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">세대 ID</span>
                      <span className="font-medium">{displayUnit?.id || unit?.id || '-'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">생성일</span>
                      <span className="font-medium">{(displayUnit?.created_at || unit?.created_at) ? new Date(displayUnit?.created_at || unit?.created_at).toLocaleDateString() : '-'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">최종 수정일</span>
                      <span className="font-medium">{(displayUnit?.updated_at || unit?.updated_at) ? new Date(displayUnit?.updated_at || unit?.updated_at).toLocaleDateString() : '-'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnitManagementModal;
